/**
 * OneFleet's realm roles (OneFleet AD-03): `of_*` realm roles in the same `onesphere` realm as the `os_*` roles,
 * registered by the OneFleet repository (`platform/keycloak/onefleet-partial-import.json`). The shell reads them
 * from `realm_access.roles` like the OneSphere roles (`lib/auth.ts`); the module and its screens gate on them
 * (`lib/onefleet/routes.ts`). Drivers and customers hold a role too, but they work in the driver app and the
 * customer portal (OneFleet repository) — in this console they reach the overview only.
 */
export const OF_ROLE = {
  admin: "of_admin",
  dispatcher: "of_dispatcher",
  fleetManager: "of_fleet_manager",
  maintenance: "of_maintenance",
  finance: "of_finance",
  analyst: "of_analyst",
  driver: "of_driver",
  customer: "of_customer",
  service: "of_service",
  /** A gate officer: sees the dispatch in front of them and releases the load (OneFleet D-160). Their screen is
   *  OneSphere's `/warehouse/gate`, not this console, so they are not in `OF_CONSOLE`. */
  security: "of_security",
} as const;

export type OfRole = (typeof OF_ROLE)[keyof typeof OF_ROLE];

/** Every OneFleet role, in the order the partial import declares them. */
export const OF_ALL: readonly OfRole[] = Object.values(OF_ROLE);

/** The roles that work in this console (the driver app and the portal serve the other three). */
export const OF_CONSOLE: readonly OfRole[] = [
  OF_ROLE.admin, OF_ROLE.dispatcher, OF_ROLE.fleetManager, OF_ROLE.maintenance, OF_ROLE.finance, OF_ROLE.analyst,
];

export const isOneFleetRole = (role: string): role is OfRole => (OF_ALL as readonly string[]).includes(role);

/** i18n keys of the role labels (`roles.<key>`), merged into `ROLE_LABEL_KEYS` by `lib/auth.ts`. */
export const OF_ROLE_LABEL_KEYS: Record<OfRole, string> = {
  of_admin: "roles.ofAdmin",
  of_dispatcher: "roles.ofDispatcher",
  of_fleet_manager: "roles.ofFleetManager",
  of_maintenance: "roles.ofMaintenance",
  of_finance: "roles.ofFinance",
  of_analyst: "roles.ofAnalyst",
  of_driver: "roles.ofDriver",
  of_customer: "roles.ofCustomer",
  of_service: "roles.ofService",
  of_security: "roles.ofSecurity",
};
